import type { U3141DocumentTypeEntry, U3141LotDetailDocument } from '../U3141Service';
import {
  U3141_DOCUMENT_TYPES_FOR_ADD_DIALOG,
  U3141_SINGLE_SLOT_DOCUMENT_TYPES,
  u3141IsMultiAddableDocumentType,
  u3141IsSingleSlotDocumentType,
} from '../U3141Service';

export function u3141LotHasDocumentOfType(
  existingDocuments: U3141LotDetailDocument[],
  documentType: string,
): boolean {
  return existingDocuments.some((d) => d.documentType === documentType);
}

/** Types proposés dans le combobox « Ajouter un document » (BL-0708 : index toujours proposé pour remplacement). */
export function u3141ResolveAddDocumentTypeOptions(
  addableTypes: readonly string[],
  existingDocuments: U3141LotDetailDocument[],
  singleSlotDocumentTypes: readonly string[] = U3141_SINGLE_SLOT_DOCUMENT_TYPES,
): string[] {
  return addableTypes.filter((type) => {
    if (u3141IsMultiAddableDocumentType(type)) {
      return true;
    }
    if (u3141IsSingleSlotDocumentType(type, singleSlotDocumentTypes)) {
      return true;
    }
    return !u3141LotHasDocumentOfType(existingDocuments, type);
  });
}

export type U3141DocumentCreateSuccessMessageKey =
  | 'msgIndexReplaced'
  | 'msgDocumentAdded'
  | 'msgDocumentsAddedCount';

export function u3141DocumentCreateSuccessMessageKey(params: {
  documentType: string;
  hadExistingSameType: boolean;
  fileCount: number;
  singleSlotDocumentTypes?: readonly string[];
}): U3141DocumentCreateSuccessMessageKey {
  const { documentType, hadExistingSameType, fileCount, singleSlotDocumentTypes = U3141_SINGLE_SLOT_DOCUMENT_TYPES } =
    params;
  if (
    hadExistingSameType &&
    u3141IsSingleSlotDocumentType(documentType, singleSlotDocumentTypes)
  ) {
    return 'msgIndexReplaced';
  }
  if (fileCount > 1) {
    return 'msgDocumentsAddedCount';
  }
  return 'msgDocumentAdded';
}

/** Vrai si le type sélectionné autorise plusieurs fichiers dans le dialogue. */
export function u3141AddDocumentDialogAllowsMultipleFiles(documentType: string | null): boolean {
  if (!documentType) {
    return true;
  }
  return u3141IsMultiAddableDocumentType(documentType);
}

export function u3141DefaultAddableDocumentTypes(): string[] {
  return [...U3141_DOCUMENT_TYPES_FOR_ADD_DIALOG];
}

/** Mappe `lotDetail.documentTypes` (graphe) vers la forme attendue par le dialogue d’ajout. */
export function u3141MapDocumentTypeEntriesToExistingDocuments(
  entries: U3141DocumentTypeEntry[] | undefined,
): U3141LotDetailDocument[] {
  if (!Array.isArray(entries)) {
    return [];
  }
  return entries
    .filter((e) => e?.documentType)
    .map((e) => ({
      id: String(e.id ?? ''),
      documentType: e.documentType,
      documentName: e.documentName,
    }));
}

/** Types proposés dans le sous-menu « Importer un document » (même règles que le combobox Ajouter). */
/** Liste affichée dans le panel : priorité GET documents, repli sur documentTypes du child (graphe / lotDetail). */
export function u3141ResolveChildLotDocumentsForPanel(
  apiList: U3141LotDetailDocument[],
  documentTypeEntries: U3141DocumentTypeEntry[] | undefined,
): U3141LotDetailDocument[] {
  if (apiList.length > 0) {
    return apiList;
  }
  return u3141MapDocumentTypeEntriesToExistingDocuments(documentTypeEntries);
}

export function u3141ResolveImportDocumentMenuTypes(
  addableDocumentTypes: readonly string[] | undefined,
  existingDocuments: U3141LotDetailDocument[],
  singleSlotDocumentTypes: readonly string[] = U3141_SINGLE_SLOT_DOCUMENT_TYPES,
): string[] {
  const addable = addableDocumentTypes?.length ? addableDocumentTypes : u3141DefaultAddableDocumentTypes();
  return u3141ResolveAddDocumentTypeOptions(addable, existingDocuments, singleSlotDocumentTypes);
}
